<template>
  <v-container fluid>
    <v-toolbar color="primary" class="header-title text-white">
      <v-toolbar-title class="title">Tax CIF Encrypt / Decrypt</v-toolbar-title>
    </v-toolbar>

    <div class="tools-workspace mt-5">
      <div class="workspace-heading mb-5">
        <div class="text-h5 font-weight-bold">CIF Encryption Tools</div>
        <div class="text-body-2 text-medium-emphasis mt-1">
          Encrypt a CIF for Tax Log use or decrypt an existing encrypted CIF. Add several CIFs at once — each becomes a chip.
        </div>
      </div>

      <v-row align="stretch">
        <v-col cols="12" lg="6">
          <v-card class="tool-card" rounded="lg" elevation="2">
            <div class="tool-card-header">
              <div class="tool-icon-wrap">
                <v-icon color="primary" size="26">mdi-lock-outline</v-icon>
              </div>
              <div>
                <div class="text-h6 font-weight-bold">Encrypt CIF</div>
                <div class="text-body-2 text-medium-emphasis mt-1">
                  Protect a plain CIF for Tax Log use.
                </div>
              </div>
            </div>

            <v-card-text class="tool-card-body pa-6">
              <CifChipInput
                ref="encryptInput"
                v-model="plainCifs"
                v-model:text="plainText"
                label="Plain CIF"
                placeholder="Enter CIF to encrypt"
                :max-items="maxCifs"
                :max-length="maxCifLength"
                :disabled="encryptLoading"
                testid="encrypt-tax-input"
                @submit="encryptCif"
                @update:model-value="onEncryptListChange"
              >
                <template #action>
                  <v-btn
                    color="primary"
                    variant="flat"
                    size="large"
                    :loading="encryptLoading"
                    :disabled="!plainCifs.length && !plainText.trim()"
                    data-testid="encrypt-tax-submit"
                    class="action-button"
                    @click="encryptCif"
                  >
                    Encrypt
                  </v-btn>
                </template>
              </CifChipInput>
              <v-alert
                v-if="encryptError"
                type="error"
                variant="tonal"
                class="mt-4"
                rounded="lg"
                data-testid="encrypt-tax-error"
              >
                {{ encryptError }}
              </v-alert>

              <div class="result-section" :class="{ 'result-section--ready': encryptedOk.length }">
                <div class="result-heading">
                  <span>Encrypted result<template v-if="encryptResults.length > 1"> ({{ encryptedOk.length }}/{{ encryptResults.length }})</template></span>
                  <div class="d-flex align-center ga-1">
                    <v-btn
                      v-if="encryptResults.length > 1 && encryptedOk.length"
                      size="small"
                      variant="text"
                      color="primary"
                      prepend-icon="mdi-content-copy"
                      data-testid="encrypt-tax-copy-all"
                      @click="copyAll(encryptResults, 'encrypted', 'encryptError')"
                    >
                      Copy all
                    </v-btn>
                    <v-icon v-if="encryptedOk.length" color="success" size="18">mdi-check-circle-outline</v-icon>
                  </div>
                </div>
                <div
                  v-if="!encryptResults.length"
                  class="result-output result-output--empty"
                  data-testid="encrypt-tax-result"
                >
                  <span>Encrypted CIF will appear here</span>
                </div>
                <div
                  v-for="(row, index) in encryptResults"
                  :key="`enc-${index}`"
                  class="result-output"
                  :data-testid="index === 0 ? 'encrypt-tax-result' : undefined"
                >
                  <div class="result-text">
                    <div v-if="encryptResults.length > 1" class="result-source">{{ row.input }}</div>
                    <span v-if="row.output">{{ row.output }}</span>
                    <span v-else class="text-error">{{ row.error }}</span>
                  </div>
                  <v-btn
                    v-if="row.output"
                    icon="mdi-content-copy"
                    variant="text"
                    size="small"
                    aria-label="Copy encrypted CIF"
                    @click="copyValue(row.output, 'Copied encrypted CIF', 'encryptError')"
                  />
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" lg="6">
          <v-card class="tool-card" rounded="lg" elevation="2">
            <div class="tool-card-header">
              <div class="tool-icon-wrap">
                <v-icon color="primary" size="26">mdi-lock-open-variant-outline</v-icon>
              </div>
              <div>
                <div class="text-h6 font-weight-bold">Decrypt CIF</div>
                <div class="text-body-2 text-medium-emphasis mt-1">
                  Reveal the original value from Tax Log.
                </div>
              </div>
            </div>

            <v-card-text class="tool-card-body pa-6">
              <CifChipInput
                ref="decryptInput"
                v-model="encryptedCifs"
                v-model:text="encryptedText"
                label="Encrypted CIF"
                placeholder="Paste encrypted CIF here"
                :chips-per-row="2"
                :max-items="maxCifs"
                :max-length="maxCifLength"
                :disabled="decryptLoading"
                testid="decrypt-tax-input"
                @submit="decryptCif"
                @update:model-value="onDecryptListChange"
              >
                <template #action>
                  <v-btn
                    color="primary"
                    variant="flat"
                    size="large"
                    :loading="decryptLoading"
                    :disabled="!encryptedCifs.length && !encryptedText.trim()"
                    data-testid="decrypt-tax-submit"
                    class="action-button"
                    @click="decryptCif"
                  >
                    Decrypt
                  </v-btn>
                </template>
              </CifChipInput>
              <v-alert
                v-if="decryptError"
                type="error"
                variant="tonal"
                class="mt-4"
                rounded="lg"
                data-testid="decrypt-tax-error"
              >
                {{ decryptError }}
              </v-alert>

              <div class="result-section" :class="{ 'result-section--ready': decryptedOk.length }">
                <div class="result-heading">
                  <span>Decrypted result<template v-if="decryptResults.length > 1"> ({{ decryptedOk.length }}/{{ decryptResults.length }})</template></span>
                  <div class="d-flex align-center ga-1">
                    <v-btn
                      v-if="decryptResults.length > 1 && decryptedOk.length"
                      size="small"
                      variant="text"
                      color="primary"
                      prepend-icon="mdi-content-copy"
                      data-testid="decrypt-tax-copy-all"
                      @click="copyAll(decryptResults, 'decrypted', 'decryptError')"
                    >
                      Copy all
                    </v-btn>
                    <v-icon v-if="decryptedOk.length" color="success" size="18">mdi-check-circle-outline</v-icon>
                  </div>
                </div>
                <div
                  v-if="!decryptResults.length"
                  class="result-output result-output--empty"
                  data-testid="decrypt-tax-result"
                >
                  <span>Plain CIF will appear here</span>
                </div>
                <div
                  v-for="(row, index) in decryptResults"
                  :key="`dec-${index}`"
                  class="result-output"
                  :data-testid="index === 0 ? 'decrypt-tax-result' : undefined"
                >
                  <div class="result-text">
                    <div v-if="decryptResults.length > 1" class="result-source">{{ row.input }}</div>
                    <span v-if="row.output">{{ row.output }}</span>
                    <span v-else class="text-error">{{ row.error }}</span>
                  </div>
                  <v-btn
                    v-if="row.output"
                    icon="mdi-content-copy"
                    variant="text"
                    size="small"
                    aria-label="Copy decrypted CIF"
                    @click="copyValue(row.output, 'Copied decrypted CIF', 'decryptError')"
                  />
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <v-snackbar v-model="snackbar" :timeout="1800" color="success">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import CifChipInput from '@/components/CifChipInput.vue';

const INVALID_ENCRYPTED_CIF_MESSAGE = 'The encrypted CIF is invalid or cannot be decrypted.';
const INVALID_PLAIN_CIF_MESSAGE = 'The CIF is invalid or cannot be encrypted.';
const ROW_ENCRYPT_ERROR = 'Cannot be encrypted';
const ROW_DECRYPT_ERROR = 'Invalid or cannot be decrypted';

// [#929] several CIFs in one go (entered as chips) — decrypt-batch takes at most 500 values of up to 500 characters each
const MAX_CIFS = 500;
const MAX_CIF_LENGTH = 500;
const ENCRYPT_CONCURRENCY = 10;

export default {
  name: 'DecryptTax',
  components: { CifChipInput },
  data() {
    return {
      encryptedCifs: [],
      encryptedText: '',
      decryptResults: [],
      decryptError: '',
      decryptLoading: false,
      plainCifs: [],
      plainText: '',
      encryptResults: [],
      encryptError: '',
      encryptLoading: false,
      snackbar: false,
      snackbarMessage: '',
      maxCifs: MAX_CIFS,
      maxCifLength: MAX_CIF_LENGTH,
    };
  },
  computed: {
    encryptedOk() {
      return this.encryptResults.filter((r) => r.output);
    },
    decryptedOk() {
      return this.decryptResults.filter((r) => r.output);
    },
  },
  methods: {
    // an emptied chip list clears the old results and errors, like the old clear (×) button did
    onEncryptListChange(list) {
      if (!list.length) {
        this.encryptResults = [];
        this.encryptError = '';
      }
    },
    onDecryptListChange(list) {
      if (!list.length) {
        this.decryptResults = [];
        this.decryptError = '';
      }
    },
    // returns an error message when the list cannot be sent, otherwise ''
    validateCifs(values) {
      if (values.length > MAX_CIFS) return `Enter at most ${MAX_CIFS} CIFs at a time (you entered ${values.length}).`;
      const tooLong = values.find((v) => v.length > MAX_CIF_LENGTH);
      if (tooLong) return `Each CIF must be at most ${MAX_CIF_LENGTH} characters: ${tooLong.slice(0, 40)}…`;
      return '';
    },
    callApi(methodName, ...args) {
      return new Promise((resolve, reject) => {
        if (!this.$call || typeof this.$call[methodName] !== 'function') {
          reject(new Error(`${methodName} is not available`));
          return;
        }
        this.$call[methodName](...args, resolve, reject);
      });
    },
    apiErrorMessage(error, defaultMessage) {
      const response = error?.response;
      const serverMessage = response?.data?.message ?? response?.data?.Message;
      if (serverMessage) return String(serverMessage);
      if (response?.status) return `${defaultMessage} HTTP status: ${response.status}.`;
      if (error?.message) return `${defaultMessage} ${error.message}`;
      return defaultMessage;
    },
    // one request for the whole list; values the server cannot decrypt are simply missing from data.items
    async decryptCif() {
      if (this.decryptLoading) return;
      const values = this.$refs.decryptInput.commit();
      if (!values.length) return;

      this.decryptResults = [];
      this.decryptError = this.validateCifs(values);
      if (this.decryptError) return;

      this.decryptLoading = true;
      try {
        const encryptedCifs = [...new Set(values)];
        const response = await this.callApi('DecryptTaxCifBatch', { encryptedCifs });
        if (response?.status !== 0) {
          this.decryptError = response?.message || INVALID_ENCRYPTED_CIF_MESSAGE;
          return;
        }
        const items = response?.data?.items ?? response?.data?.Items ?? [];
        const plainByCipher = new Map(
          items.map((item) => [item.encryptedCif ?? item.EncryptedCif, item.decryptedCif ?? item.DecryptedCif])
        );
        const rows = values.map((input) => {
          const plain = plainByCipher.get(input);
          return plain ? { input, output: String(plain), error: '' } : { input, output: '', error: ROW_DECRYPT_ERROR };
        });
        // a single value keeps the old look: the error goes in the alert, not in a result row
        if (rows.length === 1 && !rows[0].output) this.decryptError = INVALID_ENCRYPTED_CIF_MESSAGE;
        else this.decryptResults = rows;
      } catch (error) {
        console.error('Tax CIF decryption request failed.', error);
        this.decryptError = this.apiErrorMessage(error, INVALID_ENCRYPTED_CIF_MESSAGE);
      } finally {
        this.decryptLoading = false;
      }
    },
    // the encrypt endpoint takes one CIF per call, so the list is sent in small parallel groups
    async encryptCif() {
      if (this.encryptLoading) return;
      const values = this.$refs.encryptInput.commit();
      if (!values.length) return;

      this.encryptResults = [];
      this.encryptError = this.validateCifs(values);
      if (this.encryptError) return;

      this.encryptLoading = true;
      try {
        const unique = [...new Set(values)];
        const outcome = new Map();
        for (let i = 0; i < unique.length; i += ENCRYPT_CONCURRENCY) {
          const group = unique.slice(i, i + ENCRYPT_CONCURRENCY);
          await Promise.all(group.map(async (cif) => outcome.set(cif, await this.encryptOne(cif))));
        }
        const rows = values.map((input) => ({ input, ...outcome.get(input) }));
        // nothing worked (no permission, server down, a single bad value) → one alert with the server's message
        if (!rows.some((r) => r.output)) {
          this.encryptError = rows[0].message || INVALID_PLAIN_CIF_MESSAGE;
        } else {
          this.encryptResults = rows;
        }
      } finally {
        this.encryptLoading = false;
      }
    },
    async encryptOne(cif) {
      try {
        const response = await this.callApi('EncryptTaxCif', { cif });
        const result = response?.data?.encryptedCif ?? response?.data?.EncryptedCif;
        if (response?.status === 0 && result) return { output: String(result), error: '' };
        return { output: '', error: ROW_ENCRYPT_ERROR, message: response?.message };
      } catch (error) {
        console.error('Tax CIF encryption request failed.', error);
        return { output: '', error: ROW_ENCRYPT_ERROR, message: this.apiErrorMessage(error, INVALID_PLAIN_CIF_MESSAGE) };
      }
    },
    // copies the successful results in the same comma format they were typed in
    copyAll(rows, label, errorTarget) {
      const ok = rows.filter((r) => r.output).map((r) => r.output);
      const skipped = rows.length - ok.length;
      const note = skipped ? ` (${skipped} failed, not copied)` : '';
      this.copyValue(ok.join(','), `Copied ${ok.length} ${label} CIFs${note}`, errorTarget);
    },
    async copyValue(value, successMessage, errorTarget) {
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
        this.snackbarMessage = successMessage;
        this.snackbar = true;
      } catch (error) {
        console.error('Tax CIF result copy failed.', error);
        this[errorTarget] = `Unable to copy the result. Copy it manually. ${error?.message || ''}`.trim();
      }
    },
  },
};
</script>

<style scoped>
.tools-workspace {
  width: calc(100% - clamp(16px, 3vw, 48px));
  max-width: 1500px;
  margin-right: auto;
  margin-left: auto;
}

.workspace-heading {
  padding: 0 4px;
}

.tool-card {
  height: 100%;
  overflow: hidden;
}

.tool-card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 22px;
  background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.08), rgba(var(--v-theme-primary), 0.02));
}

.tool-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  border-radius: 12px;
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 4px 14px rgba(41, 67, 130, 0.12);
}

.tool-card-body {
  display: flex;
  flex-direction: column;
  min-height: 258px;
}

/* same button as before, now to the right of the chip box */
.action-button {
  height: 56px !important;
  width: 132px;
}

.result-section {
  margin-top: 24px;
  padding: 16px 18px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: #f8f9fc;
  transition: border-color 0.2s ease;
}

.result-section--ready {
  border-left: 3px solid rgb(var(--v-theme-success));
}

.result-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.875rem;
  font-weight: 600;
}

.result-output {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 54px;
  margin-top: 10px;
  padding: 6px 10px 6px 14px;
  border: 1px solid rgba(var(--v-border-color), 0.55);
  border-radius: 4px;
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  overflow-wrap: anywhere;
  user-select: text;
}

.result-output--empty {
  color: rgba(var(--v-theme-on-surface), 0.45);
}

.result-text {
  min-width: 0;
}

.result-source {
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface), 0.55);
}

@media (max-width: 600px) {
  .tool-card-header,
  .tool-card-body {
    padding: 18px !important;
  }

  .tool-card-body {
    min-height: 0;
  }
}
</style>
